import { useCallback, useEffect, useState, useRef } from "react";
import { queryAgendaWindow } from "@/lib/agendaQueries";
import { queryNextAgendaEvent, type NextAgendaEvent } from "@/lib/nextAgendaEvent";
import { agendaEventRange } from "@/lib/agendaEventRange";
import { supabase } from "@/integrations/supabase/client";
import { useTasks, type Task, type UpdateTaskInput } from "@/hooks/useTasks";
import { subscribeAgendaChanges } from "@/lib/agendaChanges";
import { useAgendaAutoRefresh } from "@/hooks/useAgendaAutoRefresh";

/**
 * Snapshot of the user's calendar state for the AgendaMiniMirror scorecard.
 *
 * Fetches in parallel (Promise.all):
 *   - count of timed starts/all-day overlaps today
 *   - count of distinct events in the seven calendar days starting today
 *   - title + start_at of the very next upcoming event
 *
 * Open tasks count is derived from useTasks() which the page already loads,
 * so no extra round-trip to Supabase is needed for it.
 *
 * A separate candidate for date-only events avoids comparing a UTC anchor to
 * a real appointment time. Counts use `count: "exact", head: true`, so only the count comes
 * back over the wire — no row payloads.
 */

export type NextEvent = NextAgendaEvent;

export interface AgendaSnapshot {
  todayCount: number;
  next7dCount: number;
  openTasksCount: number;
  nextEvent: NextEvent | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  refreshTasks: () => Promise<void>;
  // Task list + CRUD from the SAME useTasks() instance that produces
  // openTasksCount. The AgendaMiniMirror tasks drawer consumes these so a
  // toggle/edit/delete updates the chip count and the drawer list together —
  // a second useTasks() instance inside the drawer would desync from this one
  // (the hook is per-instance local state, not a shared cache).
  tasks: Task[];
  toggleTask: (id: string) => Promise<void>;
  updateTask: (id: string, fields: UpdateTaskInput) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
}

export function useAgendaSnapshot(): AgendaSnapshot {
  const { tasks, loading: tasksLoading, toggleTask, updateTask, deleteTask, fetchTasks } = useTasks();
  const refreshTasks = useCallback(() => fetchTasks(true), [fetchTasks]);
  const openTasksCount = tasks.filter((t) => !t.done).length;

  const [todayCount, setTodayCount] = useState(0);
  const [next7dCount, setNext7dCount] = useState(0);
  const [nextEvent, setNextEvent] = useState<NextEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);

  const requestRef = useRef(0);
  const ownerRef = useRef<string | null | undefined>(undefined);
  const generation = useRef(0);
  const mounted = useRef(true);
  const fetchSnapshotRef = useRef<(() => Promise<void>) | null>(null);
  useEffect(() => {
    mounted.current = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const owner = session?.user.id ?? null;
      if (owner !== ownerRef.current) {
        generation.current++; requestRef.current++;
        setTodayCount(0); setNext7dCount(0); setNextEvent(null); setCheckedAt(null); setError(null); setLoading(!!owner);
        // INITIAL_SESSION can arrive after the first read started. Invalidating
        // that read must also schedule its replacement outside the auth lock.
        const epoch = generation.current;
        if (owner) queueMicrotask(() => {
          if (mounted.current && ownerRef.current === owner && epoch === generation.current) void fetchSnapshotRef.current?.();
        });
      }
      ownerRef.current = owner;
    });
    const epoch = generation, reads = requestRef;
    return () => { mounted.current = false; epoch.current++; reads.current++; subscription.unsubscribe(); };
  }, []);
  const fetchSnapshot = useCallback(async () => {
    const request = ++requestRef.current;
    const epoch = generation.current;
    const assert = () => { if (!mounted.current || epoch !== generation.current || request !== requestRef.current) throw new Error("invalid_session"); };
    try {
      setLoading(true);
      setError(null);

      assert();
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      assert();
      if (authError || !user) throw authError ?? new Error("Session unavailable");
      if (ownerRef.current && ownerRef.current !== user.id) throw new Error("invalid_session");
      ownerRef.current = user.id;
      const session = await supabase.auth.getSession(); assert();
      if (session.error || session.data.session?.user.id !== user.id || !session.data.session.access_token) throw new Error("invalid_session");
      const guard = { accessToken: session.data.session.access_token, assertCurrent: assert };
      const now = new Date();
      const today = agendaEventRange("today", now);
      const week = agendaEventRange("week", now);
      const [todayRes, next7Res, nextEvRes] = await Promise.all([
        queryAgendaWindow(user.id, new Date(today.start), new Date(today.end), true, false, guard),
        queryAgendaWindow(user.id, new Date(week.start), new Date(week.end), true, false, guard),
        queryNextAgendaEvent(user.id, now, guard),
      ]);

      assert();
      const current = await supabase.auth.getUser(); assert();
      if (current.error || current.data.user?.id !== user.id) throw new Error("invalid_session");
      const firstErr = todayRes.error || next7Res.error || nextEvRes.error;
      if (firstErr) throw firstErr;
      if (request !== requestRef.current) return;
      setTodayCount(todayRes.count ?? 0);
      setNext7dCount(next7Res.count ?? 0);
      setNextEvent(nextEvRes.data);
      setCheckedAt(now.getTime());
    } catch (e) {
      if (mounted.current && epoch === generation.current) console.error("[AgendaSnapshot] load failed:", e);
      if (mounted.current && epoch === generation.current && request === requestRef.current) setError(e instanceof Error ? e.message : "Agenda unavailable");
    } finally {
      if (mounted.current && epoch === generation.current && request === requestRef.current) setLoading(false);
    }
  }, []);

  fetchSnapshotRef.current = fetchSnapshot;

  useEffect(() => {
    fetchSnapshot();
    return () => { requestRef.current += 1; };
  }, [fetchSnapshot]);

  useEffect(() => subscribeAgendaChanges(owner => { if (owner === ownerRef.current) void fetchSnapshot(); }), [fetchSnapshot]);

  useAgendaAutoRefresh({ checkedAt, nextEvent, loading, error: error !== null, refresh: fetchSnapshot });

  return {
    todayCount,
    next7dCount,
    openTasksCount,
    nextEvent,
    loading: loading || tasksLoading,
    error,
    refresh: fetchSnapshot,
    refreshTasks,
    tasks,
    toggleTask,
    updateTask,
    deleteTask,
  };
}
